export const CODE_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
export type CodeKind = 'B' | 'P';

// Odd weights are invertible mod 32, so every single-character substitution changes the check character.
const WEIGHTS = [1, 3, 5, 7, 9, 11, 13, 15, 17] as const;
const LOOKALIKE: Record<string, string> = { O: '0', I: '1', L: '1' };

function valueOf(ch: string): number {
  const v = CODE_ALPHABET.indexOf(ch);
  if (v < 0) throw new Error(`invalid code character "${ch}"`);
  return v;
}

/** Check character for a 9-character body (kind + 8 characters). */
export function checkChar(body: string): string {
  if (body.length !== 9) throw new Error('code body must be 9 characters');
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += valueOf(body[i]!) * WEIGHTS[i]!;
  return CODE_ALPHABET[sum % 32]!;
}

export function generateCode(kind: CodeKind, randomBytes: (n: number) => Uint8Array): string {
  const bytes = randomBytes(8);
  let body: string = kind;
  for (let i = 0; i < 8; i++) body += CODE_ALPHABET[bytes[i]! % 32]!;
  return body + checkChar(body);
}

/** Accepts scanned or typed input; returns the canonical 10-character code or null if it is not a valid code. */
export function normalizeCode(raw: string): string | null {
  const s = raw
    .trim()
    .toUpperCase()
    .replace(/[\s-]/g, '')
    .replace(/[OIL]/g, (c) => LOOKALIKE[c]!);
  if (s.length !== 10) return null;
  if (s[0] !== 'B' && s[0] !== 'P') return null;
  for (const ch of s) if (!CODE_ALPHABET.includes(ch)) return null;
  return checkChar(s.slice(0, 9)) === s[9] ? s : null;
}

export function formatCode(code: string): string {
  return `${code.slice(0, 5)}-${code.slice(5)}`;
}

export function codeKind(code: string): CodeKind {
  return code[0] === 'P' ? 'P' : 'B';
}

/**
 * The codes a test label carries. They are outside the generator's pattern by format: every
 * generated code is 10 characters of CODE_ALPHABET starting with B or P, and these are neither
 * 10 characters long nor start with B or P. So no real sticker, printed before or after, can read
 * as a test one, and `normalizeCode` refuses both. A scan of one is refused (or, in Look up,
 * answered "Test sticker") before any box is looked up.
 */
export const TEST_CODES = { box: 'TEST-BOX', pallet: 'TEST-PALLET' } as const;

/** Which test label this is, or null when it is not one. Case, spaces and hyphens don't matter. */
export function testCodeKind(raw: string): keyof typeof TEST_CODES | null {
  const s = raw.trim().toUpperCase().replace(/[\s-]/g, '');
  return s === 'TESTBOX' ? 'box' : s === 'TESTPALLET' ? 'pallet' : null;
}

/** What Look up answers for a test label. */
export const TEST_STICKER_LOOKUP = 'Test sticker. It checks the printer and is not a box.';
